import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCw, Construction, ArrowRight } from "lucide-react";
import { Modal } from "../UI/Modal";
import { FormField, Textarea } from "../UI/FormField";
import Button from "../UI/Button";
import {
  Work,
  WorkDraft,
  WorkPriority,
  WorksCatalog,
  WorkType,
} from "../../types/works";
import { WORK_TYPES } from "../../constants/worksConstants";
import { LocationField } from "./LocationField";
import { CustomerCombobox } from "./CustomerCombobox";

interface WorkFormModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  initialWork?: Work | null;
  catalog: WorksCatalog | null;
  onClose: () => void;
  onSubmit: (draft: WorkDraft) => Promise<void> | void;
}

/** ISO datetime <-> <input type="datetime-local"> value (local time, no seconds). */
const isoToLocalInput = (iso?: string) => {
  if (!iso) return "";
  const date = new Date(iso);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};
const localInputToIso = (value: string) =>
  value ? new Date(value).toISOString() : undefined;

const emptyDraft = (): WorkDraft => ({
  type: "cyclic",
  priority: "normal",
  plannedStart: new Date().toISOString(),
  services: [],
});

const workToDraft = (work: Work): WorkDraft => ({
  type: work.type,
  customerId: work.customerId,
  customerName: work.customerName,
  primaryObject: work.primaryObject,
  secondaryObject: work.secondaryObject,
  material: work.material,
  driverId: work.driverId,
  vehicleId: work.vehicleId,
  tonnagePlanned: work.tonnage.planned,
  distancePlannedKm: work.distanceKm.planned,
  hoursPlanned: work.hours.planned,
  roundsPlanned: work.roundsPlanned,
  priority: work.priority,
  remarks: work.remarks,
  plannedStart: work.plannedStart,
  plannedEnd: work.plannedEnd,
  services: work.services,
  assignToSelf: work.createdByRole === "driver_self",
});

const TYPE_ICONS: Record<WorkType, React.ReactNode> = {
  cyclic: <RotateCw size={13} />,
  object: <Construction size={13} />,
  pickup_delivery: <ArrowRight size={13} />,
};

/** "Badge + control" wrapper — mirrors the reference mockup's `.input-prefix`. */
const PrefixField: React.FC<{
  prefix: React.ReactNode;
  error?: boolean;
  children: React.ReactNode;
}> = ({ prefix, error, children }) => (
  <div
    className={`flex items-stretch h-[34px] sm:h-[38px] rounded-lg border bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[#C97A3C] focus-within:border-transparent ${
      error ? "border-[#E24B4A]" : "border-[rgba(11,15,14,0.15)]"
    }`}
  >
    <span className="flex items-center justify-center min-w-[28px] sm:min-w-[38px] px-1.5 sm:px-2 bg-[#F5F4F0] border-r border-[#ECEAE4] text-[10px] font-semibold uppercase tracking-wide text-[#8A9590] shrink-0">
      {prefix}
    </span>
    {children}
  </div>
);

const prefixControlClass =
  "flex-1 min-w-0 h-full border-0 bg-transparent px-3 text-[13px] text-[#0B0F0E] focus:outline-none focus:ring-0 appearance-none";

export const WorkFormModal: React.FC<WorkFormModalProps> = ({
  isOpen,
  mode,
  initialWork,
  catalog,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation("works");
  const [draft, setDraft] = useState<WorkDraft>(emptyDraft());
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const drivers = catalog?.drivers ?? [];
  const vehicles = catalog?.vehicles ?? [];

  useEffect(() => {
    if (isOpen) {
      setDraft(initialWork ? workToDraft(initialWork) : emptyDraft());
      setErrors({});
    }
  }, [isOpen, initialWork]);

  const set = <K extends keyof WorkDraft>(key: K, value: WorkDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const showSecondaryObject = draft.type !== "object";

  const validate = () => {
    const next: Record<string, string> = {};
    if (!draft.plannedStart) next.plannedStart = t("form.errors.required");
    if (!draft.primaryObject) next.primaryObject = t("form.errors.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit(draft);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? t("form.createTitle") : t("form.editTitle")}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-3 py-2.5 sm:p-5 space-y-3 sm:space-y-5">
          {/* Type selector */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-[#8A9590] shrink-0">
              {t("form.fields.type")}:
            </span>
            <div className="overflow-x-auto min-w-0">
              <div className="inline-flex items-center gap-0.5 bg-[#F3F1EC] p-0.5 rounded-lg">
                {WORK_TYPES.map((type) => {
                  const active = draft.type === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => set("type", type)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 sm:px-3.5 sm:py-1.5 rounded-md text-[11px] sm:text-[12.5px] font-medium transition-colors whitespace-nowrap ${
                        active
                          ? "bg-white text-[#2B2F2E] shadow-sm"
                          : "text-[#8A9590] hover:text-[#5A6460]"
                      }`}
                    >
                      {TYPE_ICONS[type]}
                      {t(`type.${type}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Core details */}
          <section className="space-y-2 sm:space-y-3">
            {/* Row 1: Customer + Material + Priority */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="col-span-2 sm:col-span-1">
                <FormField label={t("form.fields.customer")}>
                  <CustomerCombobox
                    customerId={draft.customerId}
                    customerName={draft.customerName}
                    onChange={(id, name) =>
                      setDraft((prev) => ({
                        ...prev,
                        customerId: id,
                        customerName: name,
                      }))
                    }
                  />
                </FormField>
              </div>
              <FormField label={t("form.fields.material")}>
                <PrefixField prefix="MT">
                  <input
                    type="text"
                    value={draft.material ?? ""}
                    onChange={(e) =>
                      set("material", e.target.value || undefined)
                    }
                    placeholder={t("form.placeholders.material")}
                    className={prefixControlClass}
                  />
                </PrefixField>
              </FormField>
              <FormField label={t("form.fields.priority")}>
                <PrefixField prefix="🚩">
                  <select
                    value={draft.priority}
                    onChange={(e) =>
                      set("priority", e.target.value as WorkPriority)
                    }
                    className={prefixControlClass}
                  >
                    {(
                      ["low", "normal", "high", "urgent"] as WorkPriority[]
                    ).map((p) => (
                      <option key={p} value={p}>
                        {t(`priority.${p}`)}
                      </option>
                    ))}
                  </select>
                </PrefixField>
              </FormField>
            </div>

            {/* Row 2: Location objects */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              <FormField
                label={t(
                  showSecondaryObject
                    ? "form.fields.destinationObject"
                    : "form.fields.object",
                )}
                required
                error={errors.primaryObject}
              >
                <LocationField
                  accent="orange"
                  value={draft.primaryObject}
                  placeholder={t("form.placeholders.select")}
                  error={!!errors.primaryObject}
                  onChange={(value) => set("primaryObject", value)}
                />
              </FormField>
              {showSecondaryObject && (
                <FormField label={t("form.fields.originObject")}>
                  <LocationField
                    accent="amber"
                    value={draft.secondaryObject}
                    placeholder={t("form.placeholders.select")}
                    onChange={(value) => set("secondaryObject", value)}
                  />
                </FormField>
              )}
            </div>

            {/* Row 3: Driver + Vehicle */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <FormField label={t("form.fields.driver")}>
                <PrefixField prefix="DR">
                  <select
                    value={draft.driverId ?? ""}
                    onChange={(e) =>
                      set("driverId", e.target.value || undefined)
                    }
                    className={prefixControlClass}
                  >
                    <option value="">
                      {t("form.placeholders.unassigned")}
                    </option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </PrefixField>
              </FormField>
              <FormField label={t("form.fields.vehicle")}>
                <PrefixField prefix="VH">
                  <select
                    value={draft.vehicleId ?? ""}
                    onChange={(e) =>
                      set("vehicleId", e.target.value || undefined)
                    }
                    className={prefixControlClass}
                  >
                    <option value="">
                      {t("form.placeholders.unassigned")}
                    </option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.displayName || v.plateNumber}{v.label ? ` · ${v.label}` : ""}
                      </option>
                    ))}
                  </select>
                </PrefixField>
              </FormField>
            </div>

            {/* Row 4: Dates */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <FormField
                label={t("form.fields.plannedStart")}
                required
                error={errors.plannedStart}
              >
                <PrefixField prefix="📅" error={!!errors.plannedStart}>
                  <input
                    type="datetime-local"
                    value={isoToLocalInput(draft.plannedStart)}
                    onChange={(e) =>
                      set(
                        "plannedStart",
                        localInputToIso(e.target.value) ?? draft.plannedStart,
                      )
                    }
                    className={prefixControlClass}
                  />
                </PrefixField>
              </FormField>
              <FormField label={t("form.fields.plannedEnd")}>
                <PrefixField prefix="🏁">
                  <input
                    type="datetime-local"
                    value={isoToLocalInput(draft.plannedEnd)}
                    onChange={(e) =>
                      set("plannedEnd", localInputToIso(e.target.value))
                    }
                    className={prefixControlClass}
                  />
                </PrefixField>
              </FormField>
            </div>
          </section>

          {/* Remarks */}
          <section>
            <FormField label={t("form.fields.remarks")}>
              <Textarea
                rows={2}
                value={draft.remarks ?? ""}
                onChange={(e) => set("remarks", e.target.value || undefined)}
                placeholder={t("form.placeholders.remarks")}
              />
            </FormField>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-3 py-2 sm:px-5 sm:py-3 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            {t("form.cancel")}
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {mode === "create" ? t("form.create") : t("form.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
